import React, { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import styles from "./EntriesBreakdownTable.module.scss";

interface IBreakdownRow {
  showType: string;
  showDate: Date;
  showName: string;
  element?: string;
  level?: string;
  section?: string;
  entryCount: number;
  isParent?: boolean;
  children?: IBreakdownRow[];
  path?: string[];
}

interface IProps {
  scores: any[];
  event: any;
}

const EntriesBreakdownTable: React.FC<IProps> = ({ scores, event }) => {
  // Aggregate scores into hierarchical breakdown rows
  const rowData = useMemo(() => {
    // First, group by show (show type + date + name)
    const showGroups = new Map<string, Map<string, number>>();
    
    scores.forEach((score) => {
      const showKey = `${score.show_type}|${score.show_date}|${score.show_name}`;
      const element = score.dog_class?.element || score.element || "-";
      const level = score.dog_class?.level || "-";
      const section = score.section || "-";
      const detailKey = `${element}|${level}|${section}`;
      
      if (!showGroups.has(showKey)) {
        showGroups.set(showKey, new Map());
      }
      
      const detailsMap = showGroups.get(showKey)!;
      detailsMap.set(detailKey, (detailsMap.get(detailKey) || 0) + 1);
    });
    
    // Build flat array with hierarchical paths
    const flatData: IBreakdownRow[] = [];
    
    showGroups.forEach((detailsMap, showKey) => {
      const [showType, showDate, showName] = showKey.split('|');
      
      // Calculate total for this show
      let totalCount = 0;
      const childRows: IBreakdownRow[] = [];
      
      detailsMap.forEach((count, detailKey) => {
        const [element, level, section] = detailKey.split('|');
        totalCount += count;
        
        childRows.push({
          showType: (Constants.show_type as any)[showType] || showType,
          showDate: new Date(showDate),
          showName,
          element,
          level,
          section,
          entryCount: count,
          isParent: false,
          path: [showKey, detailKey],
        });
      });
      
      // Sort children by element, level, section
      childRows.sort((a, b) => {
        if (a.element !== b.element) return (a.element || "").localeCompare(b.element || "");
        if (a.level !== b.level) return (a.level || "").localeCompare(b.level || "");
        return (a.section || "").localeCompare(b.section || "");
      });
      
      // Add parent row
      flatData.push({
        showType: (Constants.show_type as any)[showType] || showType,
        showDate: new Date(showDate),
        showName,
        entryCount: totalCount,
        isParent: true,
        path: [showKey],
      });
      
      // Add all children
      flatData.push(...childRows);
    });
    
    // Sort by show date and type (parent rows will naturally group with their children)
    return flatData;
  }, [scores]);

  const columnDefs: ColDef[] = [
    {
      field: "showDate",
      headerName: "Show Date",
      width: 185,
      filter: true,
      valueFormatter: (params) => {
        if (!params.value) return "";
        return moment(params.value).format("MMM DD, YYYY");
      },
    },
    {
      field: "showName",
      headerName: "Show Name",
      width: 194,
      filter: true,
    },
    {
      field: "element",
      headerName: "Element",
      width: 151,
      filter: true,
      cellStyle: (params: any) => {
        if (params.data?.isParent) {
          return { color: 'transparent' };
        }
        return null;
      },
    },
    {
      field: "level",
      headerName: "Level",
      width: 155,
      filter: true,
      cellStyle: (params: any) => {
        if (params.data?.isParent) {
          return { color: 'transparent' };
        }
        return null;
      },
    },
    {
      field: "section",
      headerName: "Section",
      width: 140,
      filter: true,
      cellStyle: (params: any) => {
        if (params.data?.isParent) {
          return { color: 'transparent' };
        }
        return null;
      },
    },
    {
      field: "entryCount",
      headerName: "Entry Count",
      width: 192,
      filter: false,
      type: "numericColumn",
      cellStyle: (params: any) => {
        const baseStyle: any = { textAlign: "right" };
        if (params.data?.isParent) {
          baseStyle.fontWeight = "700";
        } else {
          baseStyle.fontWeight = "500";
        }
        return baseStyle;
      },
    },
  ];

  return (
    <div className={styles.EntriesBreakdownTable}>
      <div className={styles.header}>
        <h2 className={styles.sectionTitle}>Entries Breakdown</h2>
      </div>

      <div className={styles.content}>
        <div className={styles.tableWrap}>
          <div className={styles.tableWrapInner}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              domLayout="autoHeight"
              treeData={true}
              getDataPath={(data) => data.path}
              autoGroupColumnDef={{
                headerName: "Show Type",
                minWidth: 200,
                cellRendererParams: {
                  suppressCount: true,
                  innerRenderer: (params: any) => {
                    return params.data?.showType || '';
                  },
                },
              }}
              groupDefaultExpanded={0}
              gridOptions={{
                defaultColDef: {
                  flex: 1,
                  minWidth: 100,
                  resizable: true
                },
                animateRows: true,
                rowHeight: 48,
                headerHeight: 48,
                suppressCellFocus: true,
                pagination: true,
                paginationPageSize: 5,
                paginationPageSizeSelector: [5, 10, 25]
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EntriesBreakdownTable;

